import { get, range } from 'lodash'
import * as Yup from 'yup'

import * as Nope from 'lib/utils/validation/Nope'
import Vehicle from './Vehicle'
import {
    getVehicleDisplayName,
} from 'lib/utils/store/vehicle'

const yearOptions = range(new Date().getFullYear() + 1, 1899).map(y => ({ name: y, value: y }))

export default class PartVehicle extends Vehicle {
    static create (vehicle) {
        return new PartVehicle(vehicle)
    }

    /**
     * Common data
     */

    getYear () {
        return get(this, 'vehicle.verticalData.year', null)
    }

    getMakeName () {
        return get(this, 'vehicle.make', null)
    }

    getModelTypeName () {
        return get(this, 'vehicle.verticalData.modelTypeName')
    }

    getModelName () {
        return get(this, 'vehicle.model', null)
    }

    getMakeAndModelName () {
        const make = this.getMakeName() || ''
        const model = this.getModelTypeName() || ''
        return `${make} ${model}`.trim()
    }

    getMakeModelAndYear () {
        const year = this.getYear()
        const makeName = this.getMakeName()
        const modelName = this.getModelName()

        return (makeName || modelName || year)
            ? `${makeName || '-'}, ${modelName || '-'}, ${year || '-'}`
            : null
    }

    /**
     * Technical data
     */

    getGrossWeight () {
        return get(this, 'vehicle.verticalData.grossWeight', null)
    }

    /**
     * Primary details
     */

    getPrimaryDetailsSummary () {
        return this.getMakeAndModelName() || '-'
    }

    getPrimaryDetailsValidationSchema () {
        return Nope.schema({
            vehicleType: Yup.string(),
            regNumber: Nope.registerNumber(),
            isFinnishRegNumber: Yup.boolean(),
            name: Nope.vehicleName(),
            make: Yup.string(),
            mfgYear: Yup.string().nullable(),
            model: Yup.string(),
        })
    }

    getPrimaryDetailsInitialValues () {
        return {
            vehicleType: this.getVehicleTypeId() || '',
            mfgYear: this.getYear() || '',
            name: this.vehicle.name || '',
            make: this.getMakeName() || '',
            model: this.getModelName() || '',
            regNumber: this.vehicle.regNumber || '',
            isFinnishRegNumber: this.getIsFinnishRegNumber(),
        }
    }

    getPrimaryDetailsFieldsLayoutSchema () {
        return {
            layout: [
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['regNumber', 'isFinnishRegNumber', 'name'],
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['make', 'model', 'mfgYear'],
                },
            ]
        }
    }

    getPrimaryDetailsFieldsSchema (t) {
        return {
            fields: [
                {
                    name: 'regNumber',
                    type: 'string',
                    inputType: 'text',
                    fieldProps: {
                        label: t('vehicleProfile:regNumber'),
                        placeholder: 'ABC-123',
                        validate: (value, formValues) => this.validateRegNumber(value, formValues),
                    },
                },
                {
                    name: 'isFinnishRegNumber',
                    type: 'boolean',
                    inputType: 'checkbox',
                    fieldProps: {
                        label: t('vehicleProfile:sections.primary.isFinnishRegNumber'),
                        size: 'large'
                    }
                },
                {
                    name: 'name',
                    type: 'string',
                    inputType: 'text',
                    fieldProps: {
                        label: t('vehicleProfile:vehicleNickname'),
                        description: t('vehicleProfile:vehicleNicknameDescription'),
                        placeholder: getVehicleDisplayName(this.vehicle, t),
                    },
                },
                {
                    name: 'make',
                    type: 'string',
                    inputType: 'text',
                    fieldProps: {
                        label: t('vehicleProfile:vehicleMake'),
                        placeholder: t('vehicleProfile:vehicleMake'),
                    },
                },
                {
                    name: 'model',
                    type: 'string',
                    inputType: 'text',
                    fieldProps: {
                        label: t('vehicleProfile:vehicleModel'),
                        placeholder: t('vehicleProfile:vehicleModel'),
                    },
                },
                {
                    name: 'mfgYear',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'value',
                        label: t('vehicleProfile:vehicleYearModel'),
                        placeholder: t('vehicleProfile:sections.primary.choose.year'),
                        loadingPlaceholder: t('vehicleProfile:sections.primary.loading.year'),
                        emptyPlaceholder: t('vehicleProfile:sections.primary.empty.year'),
                        options: yearOptions,
                    },
                },
            ]
        }
    }

     /**
     * Technical details
     */

    getTechnicalDetailsValidationSchema () {
        return Nope.schema({
            grossWeight: Nope.positiveInteger(),
        })
    }

    getTechnicalDetailsInitialValues () {
        return {
            grossWeight: this.getGrossWeight() || '',
        }
    }

    getTechnicalDetailsFieldsLayoutSchema (t) {
        return {
            layout: [
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['grossWeight'],
                },
            ]
        }
    }

    getTechnicalDetailsFieldsSchema (t) {
        return {
            fields: [
                {
                    name: 'grossWeight',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:grossWeight')} (kg)`,
                    },
                },
            ],
        }
    }
}
